'use client';

import { useCallback, useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import classNames from 'classnames';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { analytics, analyticsEvents } from 'lib/analytics';
import locationStore from 'stores/LocationStore';
import { IconClose } from '@sympla/sympla-components';
import IconSearch from 'assets/icons/ic-search.svg';
import SearchSuggestions from 'components/SearchSuggestions/SearchSuggestions';
import { ANYWHERE_LOCATION_JSON } from 'constants/index';
import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import { isMobile as getIsMobile } from 'utils/misc';
import { labelsAnalyticsTags } from 'constants/algolia';

import * as styles from './AlgoliaSearch.css';

const SEARCH_HASH = '#pesquisar';

function AlgoliaSearch({
  isNavbarCollapsed = false,
  isDark = false,
  hasMossColor = false
}: {
  isNavbarCollapsed?: boolean;
  isDark?: boolean;
  hasMossColor?: boolean;
}) {
  const [inputValue, setInputValue] = useState('');

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchParams = useSearchParams();
  const currentSearchParams = searchParams || new URLSearchParams();
  const searchText = currentSearchParams.get('s');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const userCity = locationStore.selectedLocation?.city ?? undefined;

  const closedResponsiveSearchValue = useFeatureValue(
    'closed-responsive-search',
    false
  );
  const isMobile = getIsMobile();
  const useNewProposalSearch = closedResponsiveSearchValue && isMobile;

  useEffect(() => {
    const checkHash = () => {
      if (typeof window !== 'undefined') {
        setIsDropdownOpen(window.location.hash === SEARCH_HASH);
      }
    };

    checkHash();

    window.addEventListener('hashchange', checkHash);

    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  useEffect(() => {
    if (isDropdownOpen && inputRef.current && !useNewProposalSearch) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isDropdownOpen, useNewProposalSearch]);

  useEffect(() => {
    if (isDropdownOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDropdownOpen]);

  const iconClass = classNames(styles.cleanInput, {
    [styles.collapsedCleanInput]: isNavbarCollapsed,
    [styles.iconMoss]: hasMossColor
  });

  const searchFormClass = classNames(styles.searchForm, {
    [styles.collapsedSearchForm]: isNavbarCollapsed,
    [styles.searchFormDark]: isDark,
    [styles.searchFormMoss]: hasMossColor
  });

  const openDropdown = useCallback(() => {
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
      if (typeof window !== 'undefined') {
        window.history.pushState(null, '', SEARCH_HASH);
      }
    }
  }, [isDropdownOpen]);

  const closeDropdown = useCallback(() => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
      if (typeof window !== 'undefined') {
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.pushState(null, '', cleanUrl);
      }
    }
  }, [isDropdownOpen]);

  const handleShowMore = useCallback(() => {
    window.location.href = createLinkToRedirect({
      param: `?s=${inputValue}`,
      routeType: 'search-bar-default-filter',
      userLocation: ANYWHERE_LOCATION_JSON
    });
  }, [inputValue]);

  function handleSearch(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.Intercom('trackEvent', 'Fez uma busca');
    handleShowMore();
  }

  function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' && inputValue.trim().length > 0) {
      window.Intercom('trackEvent', 'Fez uma busca');
      handleShowMore();
    } else if (event.key && !isDropdownOpen) {
      openDropdown();
    }
  }

  useEffect(() => {
    setInputValue(searchText || '');
  }, [searchText]);

  const handleClickInside = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleClickOutside = () => {
    closeDropdown();
  };

  const handleCloseDropdown = useCallback(() => {
    closeDropdown();
  }, [closeDropdown]);

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
    if (!isDropdownOpen) {
      openDropdown();
      analytics.sendEvent(analyticsEvents.search_started, {
        search_page_name: document.title,
        is_bar_empty: !inputValue.length,
        search_current_pre_filled_term: inputValue || event.target.placeholder,
        search_location_set: userCity
      });
    }
  };

  const getSearchLabelTag = () => {
    if (!isMobile) return '';
    return useNewProposalSearch
      ? labelsAnalyticsTags.newProposalSearch
      : labelsAnalyticsTags.currentSearch;
  };

  return (
    <div
      className={`${styles.searchWrapper} ${
        isNavbarCollapsed && styles.collapsedSearchWrapper
      } `}
      data-testid="search-algolia-wrapper"
    >
      <form
        ref={formRef}
        className={searchFormClass}
        onSubmit={handleSearch}
        role="presentation"
        data-testid="search-algolia-form"
      >
        <IconSearch
          width={24}
          height={24}
          className={classNames(styles.searchIcon, {
            [styles.searchIconDark]: isDark,
            [styles.collapsedSearchIcon]: isNavbarCollapsed
          })}
        />
        <input
          ref={inputRef}
          id="search-experiences"
          className={classNames(styles.searchInput, {
            [styles.searchInputDark]: isDark
          })}
          placeholder="Buscar experiências"
          type="text"
          onChange={handleTextChange}
          value={inputValue}
          onKeyDown={handleKeyDown}
          data-testid="search-algolia-input"
          onFocus={handleFocus}
          readOnly={!!useNewProposalSearch}
          inputMode={useNewProposalSearch ? 'none' : 'text'}
        />
        {inputValue && (
          <div className={styles.searchButtons}>
            <IconClose
              width={16}
              height={16}
              className={iconClass}
              onClick={() => {
                setInputValue('');
                inputRef.current?.focus();
              }}
            />
          </div>
        )}
      </form>
      {isDropdownOpen && (
        <SearchSuggestions
          onClickInside={handleClickInside}
          setInputValue={setInputValue}
          handleTextChange={handleTextChange}
          inputValue={inputValue}
          inputRef={inputRef}
          onClose={handleCloseDropdown}
          formRef={formRef}
          hasMossColor={hasMossColor}
          handleSearch={handleSearch}
          isNavbarCollapsed={isNavbarCollapsed}
          handleClickOutside={handleClickOutside}
          useNewProposalSearch={useNewProposalSearch}
          labelTagsAnalytics={[getSearchLabelTag()]}
        />
      )}
    </div>
  );
}

export default AlgoliaSearch;
